import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth, API } from '../App';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  FileText, Download, Loader2, ArrowLeft, Share2, Printer
} from 'lucide-react';
import { toast } from 'sonner';
import Header from '../components/Header';
import Footer from '../components/Footer';

const APPLICATION_AREAS = [
  { value: 'countertop', label: 'Countertop' },
  { value: 'floor', label: 'Floor' },
  { value: 'wall', label: 'Wall Cladding' },
  { value: 'bathroom', label: 'Bathroom' },
  { value: 'fireplace', label: 'Fireplace' },
  { value: 'outdoor', label: 'Outdoor' },
  { value: 'pool', label: 'Pool Area' },
  { value: 'stairs', label: 'Stairs' },
  { value: 'facade', label: 'Facade' },
];

const MaterialSpecification = () => {
  const { materialId } = useParams();
  const { user } = useAuth();
  
  const [material, setMaterial] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [projectContext, setProjectContext] = useState('');
  const [applicationArea, setApplicationArea] = useState('');
  const [generatedPdf, setGeneratedPdf] = useState(null);

  useEffect(() => {
    const fetchMaterial = async () => {
      try {
        const response = await axios.get(`${API}/materials/${materialId}`, { withCredentials: true });
        setMaterial(response.data);
      } catch (error) {
        console.error('Failed to fetch material:', error);
        toast.error('Failed to load material');
      } finally {
        setLoading(false);
      }
    };

    if (materialId) {
      fetchMaterial();
    }
  }, [materialId]);

  const generateSpecification = async () => {
    setGenerating(true);
    try {
      const params = new URLSearchParams();
      if (projectContext) params.append('project_context', projectContext);
      if (applicationArea) params.append('application_area', applicationArea);

      const response = await axios.post(
        `${API}/specifications/generate/${materialId}?${params.toString()}`,
        {},
        { withCredentials: true }
      );

      setGeneratedPdf(response.data);
      toast.success('Specification sheet generated!');
    } catch (error) {
      console.error('Failed to generate specification:', error);
      toast.error('Failed to generate specification');
    } finally {
      setGenerating(false);
    }
  };

  const downloadPdf = () => {
    if (!generatedPdf) return;

    const link = document.createElement('a');
    link.href = `data:application/pdf;base64,${generatedPdf.pdf_base64}`;
    link.download = generatedPdf.filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const printPdf = () => {
    if (!generatedPdf) return;

    const pdfWindow = window.open('');
    pdfWindow.document.write(`
      <html>
        <head><title>${generatedPdf.filename}</title></head>
        <body style="margin:0">
          <embed width="100%" height="100%" src="data:application/pdf;base64,${generatedPdf.pdf_base64}" type="application/pdf" />
        </body>
      </html>
    `);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!material) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="max-w-4xl mx-auto px-6 py-12 pt-28 text-center">
          <h1 className="text-2xl font-medium mb-4">Material Not Found</h1>
          <Link to="/materials">
            <Button variant="outline">
              <ArrowLeft className="w-4 h-4 mr-2" /> Back to Materials
            </Button>
          </Link>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Header />
      
      <main className="flex-1 max-w-4xl mx-auto px-6 py-12 pt-28 w-full">
        {/* Back Link */}
        <Link 
          to={`/materials/${materialId}`} 
          className="inline-flex items-center text-muted-foreground hover:text-foreground mb-8"
        >
          <ArrowLeft className="w-4 h-4 mr-2" /> Back to Material
        </Link>

        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <FileText className="w-8 h-8 text-primary" />
            <h1 className="heading-display text-3xl font-medium">Material Specification</h1>
          </div>
          <p className="text-muted-foreground">
            Generate a professional specification sheet for this material
          </p>
        </div>

        {/* Material Preview */}
        <div className="bg-secondary/30 p-6 mb-8 flex gap-6">
          {material.images?.[0] && (
            <img 
              src={material.images[0]} 
              alt={material.name}
              className="w-32 h-32 object-cover"
            />
          )}
          <div>
            <h2 className="text-xl font-medium mb-1">{material.name}</h2>
            <p className="text-muted-foreground mb-2">{material.stone_type}</p>
            <p className="text-sm text-muted-foreground">Origin: {material.origin || 'N/A'}</p>
            <p className="text-sm text-muted-foreground">Color: {material.color}</p>
          </div>
        </div>

        {/* Configuration */}
        {!generatedPdf && (
          <div className="space-y-6 mb-8">
            <h3 className="text-lg font-medium">Customize Specification</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <Label className="mb-2 block">Project Name (optional)</Label>
                <Input
                  value={projectContext}
                  onChange={(e) => setProjectContext(e.target.value)}
                  placeholder="e.g., Smith Residence Kitchen"
                  className="input-field"
                />
              </div>
              <div>
                <Label className="mb-2 block">Application Area (optional)</Label>
                <Select value={applicationArea} onValueChange={setApplicationArea}>
                  <SelectTrigger className="input-field">
                    <SelectValue placeholder="Select application..." />
                  </SelectTrigger>
                  <SelectContent>
                    {APPLICATION_AREAS.map(area => (
                      <SelectItem key={area.value} value={area.value}>{area.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <Button 
              onClick={generateSpecification}
              disabled={generating}
              className="btn-primary px-8"
            >
              {generating ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating...</>
              ) : (
                <><FileText className="w-4 h-4 mr-2" /> Generate Specification Sheet</>
              )}
            </Button>
          </div>
        )}

        {/* Generated PDF Preview */}
        {generatedPdf && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-medium">Specification Ready</h3>
              <Button variant="outline" onClick={() => setGeneratedPdf(null)}>
                Generate New
              </Button>
            </div>

            {/* PDF Preview */}
            <div className="border bg-white p-4">
              <embed
                src={`data:application/pdf;base64,${generatedPdf.pdf_base64}`}
                type="application/pdf"
                width="100%"
                height="500px"
                className="border"
              />
            </div>

            {/* Actions */}
            <div className="flex gap-4">
              <Button onClick={downloadPdf} className="btn-primary">
                <Download className="w-4 h-4 mr-2" /> Download PDF
              </Button>
              <Button variant="outline" onClick={printPdf}>
                <Printer className="w-4 h-4 mr-2" /> Print
              </Button>
              <Button variant="outline" onClick={() => {
                navigator.clipboard.writeText(`${window.location.origin}/specifications/${materialId}`);
                toast.success('Link copied to clipboard');
              }}>
                <Share2 className="w-4 h-4 mr-2" /> Share Link
              </Button>
            </div>
          </div>
        )}

        {/* Info */}
        <div className="mt-12 p-6 bg-secondary/20 text-sm text-muted-foreground">
          <h4 className="font-medium text-foreground mb-2">About Specification Sheets</h4>
          <p>
            Specification sheets include material properties, available finishes, recommended applications, 
            and care instructions. These documents are perfect for project documentation, client presentations, 
            and contractor specifications.
          </p>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default MaterialSpecification;
